import { useCallback, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

export function useTheme() {
	const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">("dark");

	const [theme, setThemeState] = useState<Theme>(() => {
		if (typeof localStorage !== "undefined") {
			return (localStorage.getItem("boring-agent-theme") as Theme) || "system";
		}
		return "system";
	});

	const applyTheme = useCallback((t: Theme) => {
		const root = document.documentElement;
		const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		const isDark = t === "dark" || (t === "system" && systemDark);
		setResolvedTheme(isDark ? "dark" : "light");

		if (isDark) {
			root.classList.remove("light");
			root.classList.add("dark");
			root.style.colorScheme = "dark";
		} else {
			root.classList.remove("dark");
			root.classList.add("light");
			root.style.colorScheme = "light";
		}
	}, []);

	useEffect(() => {
		applyTheme(theme);
		localStorage.setItem("boring-agent-theme", theme);
	}, [theme, applyTheme]);

	useEffect(() => {
		const listener = (_e: MediaQueryListEvent) => {
			if (theme === "system") applyTheme("system");
		};
		window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", listener);
		return () =>
			window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", listener);
	}, [theme, applyTheme]);

	const setTheme = useCallback((t: Theme) => {
		setThemeState(t);
	}, []);

	return { theme, setTheme, resolvedTheme };
}
